import React, { useEffect, useMemo, useState } from 'react'
import cx from 'classnames'
import { Button, TextField, TextInput } from '../../../../views/components'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import { BillingAgencySetting } from '../../../api/accounting/types/BillingAgencySettingsResponse'
import styles from './DiscountPanel.module.scss'

// Up to two decimals of a percent, so 54.55% round-trips exactly.
const PERCENT_INPUT = /^\d{0,3}(\.\d{0,2})?$/

// Matches the reporting-api limit.
const MAX_NOTES_LENGTH = 2000

export type DiscountTarget = {
  agencyId: string
  agencyName: string
  tradingAs?: string | null
  /** Absent when adding an agency that has no settings row yet. */
  existing?: BillingAgencySetting
}

type TProps = {
  target: DiscountTarget
  saving?: boolean
  onCancel: () => void
  /** A null discount is blank (new or cancelled agency), distinct from 0%. */
  onSave: (values: {
    agencyId: string
    processing: number | null
    transaction: number | null
    notes: string | null
  }) => void
  onRemove?: (agencyId: string) => void
}

// Stored as a fraction, entered as a percent. 0.5455 <-> 54.55. Blank <-> null.
const toPercent = (fraction: number | null) => (fraction === null ? '' : String(Number((fraction * 100).toFixed(2))))
const toFraction = (percent: string) => (percent === '' ? null : Number((Number(percent) / 100).toFixed(6)))

const DiscountPanel = ({ target, saving = false, onCancel, onSave, onRemove }: TProps) => {
  const [processing, setProcessing] = useState('')
  const [transaction, setTransaction] = useState('')
  const [notes, setNotes] = useState('')
  const [confirmingRemove, setConfirmingRemove] = useState(false)

  useEffect(() => {
    setProcessing(target.existing ? toPercent(target.existing.percentProcessingFeeDiscount) : '')
    setTransaction(target.existing ? toPercent(target.existing.percentTransactionFeeDiscount) : '')
    setNotes(target.existing?.notes ?? '')
    setConfirmingRemove(false)
  }, [target])

  const error = useMemo(() => {
    for (const [label, raw] of [
      ['Processing', processing],
      ['Transaction', transaction],
    ] as const) {
      if (raw === '') continue
      if (!PERCENT_INPUT.test(raw) || raw === '.') return `${label} must be a percentage`
      if (Number(raw) > 100) return `${label} cannot exceed 100%`
    }
    if (notes.trim().length > MAX_NOTES_LENGTH) return `Notes cannot exceed ${MAX_NOTES_LENGTH} characters`
    return null
  }, [processing, transaction, notes])

  const canSave = !error && !saving

  return (
    <form
      className={styles.root}
      onSubmit={event => {
        event.preventDefault()
        if (!canSave) return
        onSave({
          agencyId: target.agencyId,
          processing: toFraction(processing),
          transaction: toFraction(transaction),
          notes: notes.trim() || null,
        })
      }}
    >
      <header className={styles.header}>
        <h2 className={styles.title}>{target.existing ? 'Edit discounts' : 'Add discounts'}</h2>
        <p className={styles.agency}>
          {target.agencyName}
          {target.tradingAs && <span className={styles.trading}>t/a {target.tradingAs}</span>}
        </p>
      </header>

      <div className={styles.body}>
        <TextField
          label="Processing fee discount"
          labelHint="%"
          error={error && error.startsWith('Processing') ? error : undefined}
          inputComponent={
            <TextInput
              type={TextFieldTypes.text}
              name="processing"
              inputMode="decimal"
              autoComplete="off"
              placeholder="Blank"
              value={processing}
              disabled={saving}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setProcessing(event.target.value.trim())}
            />
          }
        />

        <div className={styles.spacer}>
          <TextField
            label="Transaction fee discount"
            labelHint="%"
            error={error && error.startsWith('Transaction') ? error : undefined}
            inputComponent={
              <TextInput
                type={TextFieldTypes.text}
                name="transaction"
                inputMode="decimal"
                autoComplete="off"
                placeholder="Blank"
                value={transaction}
                disabled={saving}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTransaction(event.target.value.trim())}
              />
            }
          />
        </div>

        <p className={styles.note}>
          100% waives the fee entirely. 0% charges it in full. Leave blank for a new or cancelled agency; it is billed
          like 0%.
        </p>

        <div className={styles.spacer}>
          <TextField
            label="Notes"
            error={error && error.startsWith('Notes') ? error : undefined}
            inputComponent={
              <textarea
                className={styles.notes}
                name="notes"
                rows={4}
                value={notes}
                disabled={saving}
                onChange={event => setNotes(event.target.value)}
              />
            }
          />
        </div>

        {target.existing && onRemove && (
          <div className={styles.remove}>
            {confirmingRemove ? (
              <>
                <p className={cx(styles.note, styles['note-danger'])} role="alert">
                  Removing the row drops this agency to no discount, so it will be charged both fees in full.
                </p>
                <div className={styles['remove-actions']}>
                  <Button type="button" secondary size="sm" onClick={() => setConfirmingRemove(false)}>
                    Keep
                  </Button>
                  <Button type="button" size="sm" onClick={() => onRemove(target.agencyId)} loading={saving}>
                    Remove discounts
                  </Button>
                </div>
              </>
            ) : (
              <Button type="button" link onClick={() => setConfirmingRemove(true)} disabled={saving}>
                Remove this agency&rsquo;s discounts
              </Button>
            )}
          </div>
        )}
      </div>

      <footer className={styles.footer}>
        <Button type="button" secondary onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSave} loading={saving}>
          Save
        </Button>
      </footer>
    </form>
  )
}

export default DiscountPanel
